'use client'

/**
 * WorkflowStepList — the generic step rail for focused full-page task
 * workflows (temperature log, onboarding wizard, production records, Menus'
 * Packaging data). Each step shows a status disc (green check when done, else
 * its number), a title, and an optional sub-label; the active step is
 * highlighted.
 *
 * Two layouts off one `orientation` prop:
 *   - `vertical`   — the desktop left rail (full rows with sub-labels).
 *   - `horizontal` — a scrollable chip strip for narrow viewports.
 *
 * Promoted verbatim from `apps/ordo/client/src/components/tasks/step-list.tsx`
 * (Oct 19, 2026) once an app outside ordo needed it: Menus' Packaging data
 * wizard is built on production records' composition, rail included.
 */

import { Check } from 'lucide-react'

import { cn } from '../cn'

export interface WorkflowStep {
  id: string
  title: string
  done: boolean
  /** Progress/status text under the title (vertical) or trailing the chip
   *  (horizontal, hidden once done — the check disc says it already). */
  subLabel?: string
}

export interface WorkflowStepListProps {
  steps: WorkflowStep[]
  selectedId: string
  onSelect: (stepId: string) => void
  orientation?: 'vertical' | 'horizontal'
  ariaLabel: string
}

export function WorkflowStepList({
  steps,
  selectedId,
  onSelect,
  orientation = 'vertical',
  ariaLabel,
}: WorkflowStepListProps) {
  if (orientation === 'horizontal') {
    return (
      <div
        className="flex gap-2 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        role="tablist"
        aria-label={ariaLabel}>
        {steps.map((step, index) => {
          const isActive = step.id === selectedId
          return (
            <button
              key={step.id}
              type="button"
              role="tab"
              aria-selected={isActive}
              onClick={() => onSelect(step.id)}
              className={cn(
                'flex shrink-0 items-center gap-2 rounded-full border px-3 py-1.5 text-sm transition-colors',
                isActive
                  ? 'border-primary bg-primary/5 font-medium text-foreground'
                  : 'border-border text-muted-foreground hover:bg-muted/60',
              )}>
              <StepIndicator index={index} done={step.done} active={isActive} />
              <span className="whitespace-nowrap">{step.title}</span>
              {!step.done && step.subLabel && (
                <span className="text-xs tabular-nums text-muted-foreground">{step.subLabel}</span>
              )}
            </button>
          )
        })}
      </div>
    )
  }

  return (
    <nav className="flex flex-col gap-0.5" aria-label={ariaLabel}>
      {steps.map((step, index) => {
        const isActive = step.id === selectedId
        return (
          <button
            key={step.id}
            type="button"
            aria-current={isActive ? 'step' : undefined}
            onClick={() => onSelect(step.id)}
            className={cn(
              'group flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors',
              isActive ? 'bg-muted' : 'hover:bg-muted/60',
            )}>
            <StepIndicator index={index} done={step.done} active={isActive} />
            <span className="min-w-0 flex-1">
              <span
                className={cn(
                  'block truncate text-sm',
                  isActive || step.done ? 'font-medium text-foreground' : 'text-foreground/90',
                )}>
                {step.title}
              </span>
              {step.subLabel && (
                <span className="mt-0.5 block text-xs tabular-nums text-muted-foreground">{step.subLabel}</span>
              )}
            </span>
          </button>
        )
      })}
    </nav>
  )
}

/** Leading status disc: green check when done, else the step number. */
function StepIndicator({ index, done, active }: { index: number; done: boolean; active: boolean }) {
  return (
    <span
      className={cn(
        'flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold transition-colors',
        done
          ? 'bg-green-100 text-green-700'
          : active
            ? 'border-2 border-primary text-primary'
            : 'border border-border text-muted-foreground',
      )}>
      {done ? <Check className="h-3.5 w-3.5" strokeWidth={2.5} /> : index + 1}
    </span>
  )
}
